<script>
    // +----------------------------------------------------------------------+
    // | NUNCA anadas un bloque <template> a este SFC.                        |
    // | La plantilla de la ficha es el HTML que escribe details.blade.php    |
    // | dentro de #product-details-app; Vue 3 lo usa como plantilla de la    |
    // | raiz (container.innerHTML) SOLO si el componente no tiene `template` |
    // | ni `render`. Un <template> aqui ganaria y dejaria la pagina en       |
    // | blanco sin ningun error. vue-loader no genera `render` para un SFC   |
    // | sin bloque <template> (vue-loader/dist/index.js:134).                |
    // | Ver docs/frontend/product-page.md.                                   |
    // +----------------------------------------------------------------------+
    import {hasNoSalePrice, materializeFegimeProduct} from "@/utils/fegime";
    import {nextQuantity, normalizeQuantity, previousQuantity, saleStep} from "@/utils/quantity";
    import {formatCurrency, formatEsNumber, formatPercentage} from "@product/format";
    import MobileImages from "@product/components/MobileImages";
    import DesktopImages from "@product/components/DesktopImages";
    import Variations from "@product/components/Variations";
    import TableVariants from "@product/components/TableVariants";
    import EquivalentProducts from "@product/components/EquivalentProducts";
    import SelledTogetherProducts from "@product/components/SelledTogetherProducts";
    import ComplementaryProducts from "@product/components/ComplementaryProducts";

    export default {
        components: {
            MobileImages,
            DesktopImages,
            Variations,
            TableVariants,
            EquivalentProducts,
            SelledTogetherProducts,
            ComplementaryProducts
        },
        props: {
            noImagePath:               { type: String,           required: true },
            routeGetWishlist:          { type: String,           required: true },
            routeCreateWishlist:       { type: String,           required: true },
            routeAddOrRemoveWishlist:  { type: String,           required: true },
            allProducts:               { type: Array,            default: () => [] },
            product:                   { type: Object,           required: true },
            variations:                { type: Array,            default: () => [] },
            characteristics:           { type: Array,            default: () => [] },
            relatedProducts:           { type: Array,            default: () => [] },
            complementaryProducts:     { type: Array,            default: () => [] },
            warehouseWithTax:          { type: Boolean,          default: true },
            userIsProfecional:         { type: Boolean,          default: false },
            breadcrumbs:               { type: Array,            default: () => [] },
            warehouses:                { type: Array,            default: () => [] },
            myWarehouseId:             { type: [Number, String], required: false },
            myStoreActive:             { type: Boolean,          required: false },
            transPeralbaPro:           { type: Object,           required: true, default: () => ({}) },
            equivalentProducts:        { type: Array,            default: () => [] },
        },
        data() {
            // ─── Cantidad inicial: sembrada ANTES de que reactive() envuelva el
            // data. El JSON del producto no trae `quantity`, y si se pone en
            // mounted() el primer render pinta el input de unidades vacío
            // (vModelText.mounted hace el.value = '' con undefined) y depende
            // de un repintado posterior para mostrar el mínimo. Aquí el objeto
            // es todavía el crudo del prop, así que la escritura es legítima y
            // el primer render coincide con el value="{{ $minQty }}" del Blade.
            //
            // OJO (Vue 3): `this.product` (prop, shallowReactive) devuelve el
            // objeto CRUDO y `this.selectedProduct` (data) su proxy. Escribir
            // por el crudo después del montaje deja el proxy sin cambio que
            // notificar (hasChanged → false) y la vista no se entera: fue el
            // bug de las unidades del canary (2026-09-06). Tras este punto,
            // escribir siempre por `this.selectedProduct`.
            const product = this.product;
            if (!product.quantity) {
                product.quantity = product.minimum > 0 ? product.minimum : 1;
            }

            return {
                localVariations: [],

                // ─── Estado inicial = lo que Blade ya dibujó ──────────────────
                // Esto garantiza que el estado de Vue coincide exactamente con el
                // HTML que el servidor envió, evitando cualquier "flash" o
                // diferencia entre el SSR y el primer render de Vue.
                selectedProduct: product,

                related_products:          this.relatedProducts,
                complementary_products:    this.complementaryProducts,
                equivalent_products:       this.equivalentProducts,
                selled_products:           [],

                // En escritorio arranca en "Documentos" si el producto tiene
                // alguno (es lo que el cliente busca en la ficha); si no, en
                // "Especificaciones". En movil son acordeones y arrancan cerrados.
                activeTab:                 window.innerWidth >= 768
                                               ? ((this.product.pdfs || []).length > 0 ? 'docs' : 'specs')
                                               : null,
                currentImageIndex:         0,
                loading:                   false,
                showInputWishlist:         false,
                isProcessing:              false,
                inputErrorName:            false,
                wishlistName:              '',
                showPills:                 false,
                tableData:                 [],
                isExpandedCharacteristics: false,
                isExpandedDescription:     false,
                showAllVariants:           false,

                // Bandera residual: Vue arranca directamente sobre el DOM que
                // Blade genero (la plantilla de la raiz es el innerHTML de
                // #product-details-app), sin "swap". La dejamos en true para no
                // romper ninguna referencia residual.
                vueReady:                  true,

                equivalent_products_loaded: true,
                selled_products_loaded:     false,

                // Detalles pesados por variante (description, characteristics,
                // pdfs, images completas): allProducts viaja "slim" y esto se
                // carga por fetch al seleccionar (get-variant-details), con
                // caché por id para no repetir peticiones.
                variantDetailsCache:        {},
            };
        },
        created() {
            try {
                // El producto de la URL llega completo: sembramos su caché para
                // que volver a él tras cambiar de variante no dispare fetch.
                this.variantDetailsCache[this.product.id] = {
                    description:     this.product.description,
                    characteristics: this.product.characteristics,
                    pdfs:            this.product.pdfs,
                    images:          this.product.images,
                };

                this.localVariations = this.variations
                    ? JSON.parse(JSON.stringify(this.variations))
                    : [];
                this.generateTableData();
            } catch (error) {
                console.error("🔴 ERROR CRÍTICO EN CREATED:", error);
            }
        },
        mounted() {
            this.equivalent_products.forEach(product => {
                if (!product.custom_quantity) {
                    product.custom_quantity = this.getMinQty(product);
                }
            });
            try {
                // ── Cantidad inicial ────────────────────────────────────────────
                // La del producto de la URL ya viene sembrada desde data(). Las
                // variantes se escriben aquí en crudo (prop) y todavía no las
                // lee ninguna vista: cuando selectVariantFromTable las convierta
                // en selectedProduct, el proxy ya verá el valor.
                const minimum = this.product.minimum > 0 ? this.product.minimum : 1;

                for (let i = 0; i < this.allProducts.length; i++) {
                    this.allProducts[i].quantity = minimum;
                }

                // ── Variaciones: marcar la seleccionada ────────────────────────
                for (let i = 0; i < this.localVariations.length; i++) {
                    this.localVariations[i].selected_variation = null;

                    for (let j = 0; j < this.localVariations[i].available_values.length; j++) {
                        const productCodesArray = this.localVariations[i].available_values[j].product_codes;
                        if (
                            productCodesArray.includes(this.selectedProduct.erp_reference) ||
                            productCodesArray.includes(this.selectedProduct.alias_code)
                        ) {
                            this.localVariations[i].selected_variation = this.localVariations[i].available_values[j];
                        }
                    }
                }

                // ── Ordenar valores de variación numéricamente ─────────────────
                for (let i = 0; i < this.localVariations.length; i++) {
                    const variation = this.localVariations[i];
                    this.localVariations[i].available_values = variation.available_values.sort((a, b) => {
                        const numA = parseFloat(a.value.replace(',', '.').replace(/[^\d.]/g, ''));
                        const numB = parseFloat(b.value.replace(',', '.').replace(/[^\d.]/g, ''));
                        return numA - numB;
                    });
                }

                // ── Carga lazy de "Vendidos juntos" ───────────────────────────
                this.$nextTick(() => {
                    const observerSelled = new IntersectionObserver((entries) => {
                        if (entries[0].isIntersecting && !this.selled_products_loaded) {
                            this.selled_products_loaded = true;
                            this.loadSelledProducts();
                            observerSelled.disconnect();
                        }
                    }, { rootMargin: '300px' });

                    const el = document.querySelector('[data-section="selled"]');
                    if (el) {
                        observerSelled.observe(el);
                    } else {
                        this.loadSelledProducts();
                    }
                });

                // Las filas SSR de características llevan v-if="false" en el Blade:
                // el compilador de Vue las descarta solo, sin limpieza manual del DOM.

            } catch (error) {
                console.error("🔴 ERROR CRÍTICO EN MOUNTED:", error);
            }
        },
        computed: {
            warehouseName() {
                const matchedWarehouse = this.warehouses.find(wh => wh.id === this.myWarehouseId);
                if (matchedWarehouse?.information?.city) {
                    return matchedWarehouse.information.city;
                }
                return this.transPeralbaPro.item1424;
            },
            backLink() {
                let link = '#';
                this.breadcrumbs.forEach(breadcrumb => {
                    if (breadcrumb.href) link = breadcrumb.href;
                });
                return link;
            },
            currentNameBack() {
                if (this.breadcrumbs.length > 0) {
                    return this.breadcrumbs[this.breadcrumbs.length - 1].name;
                }
                return '';
            },
            visibleVariants() {
                if (this.showAllVariants) return this.tableData;
                return this.tableData.slice(0, 5);
            },
            totals() {
                return 0;
            },
            variationLabel() {
                const count = this.allProducts.length;
                if (count >= 100) return "+100";
                if (count >= 50)  return "+50";
                if (count >= 30)  return "+30";
                if (count >= 25)  return "+25";
                if (count >= 20)  return "+20";
                if (count >= 15)  return "+15";
                if (count >= 10)  return "+10";
                if (count >= 5)   return "+5";
                if (count >= 3)   return "+3";
                if (count >= 2)   return "+2";
                return "+100";
            },
            characteristicsGrouped() {
                const groups = {};
                if (!this.selectedProduct?.characteristics) return groups;
                this.selectedProduct.characteristics.forEach(attr => {
                    const groupName = attr.group_name || '';
                    if (!groups[groupName]) groups[groupName] = [];
                    groups[groupName].push(attr);
                });
                return groups;
            },
            characteristicsList() {
                if (this.selectedProduct?.characteristics) {
                    return this.selectedProduct.characteristics;
                }
                return [];
            },
        },
        // OJO: `filters` ya no es una opcion de Vue 3, pero se queda aqui
        // porque es el contrato con el markup de details.blade.php, que llama
        // a $options.filters.currency/percentage en seis sitios (una simple
        // lectura de propiedad, identica en Vue 2 y en Vue 3). Ese markup lo
        // comparten la rama Vue 3 y el bundle congelado de Vue 2: renombrar
        // estos dos nombres rompe las dos a la vez.
        // Los dos cuerpos viven en @product/format.js, que es el espejo byte a
        // byte de lo que imprimen las replicas SSR del Blade.
        filters: {
            currency: formatCurrency,
            percentage: formatPercentage,
        },
        methods: {
            openLogin() {
                window.dispatchEvent(new CustomEvent('open-login-modal'));
            },

            getMinQty(product) {
                return saleStep(product);
            },

            increaseQty(product) {
                product.custom_quantity = nextQuantity(product.custom_quantity, saleStep(product));
            },

            // Cantidad escrita a mano: se sube al multiplo de venta mas cercano
            // (multiplo 4, escribe 10 ⇒ 12). Ver resources/js/utils/quantity.js.
            validateInput(product) {
                product.custom_quantity = normalizeQuantity(product.custom_quantity, saleStep(product));
            },

            decreaseQty(product) {
                product.custom_quantity = previousQuantity(product.custom_quantity, saleStep(product));
            },

            // La wishlist recibe la misma cantidad valida que el carrito.
            wishlistQty(product) {
                return normalizeQuantity(
                    product.custom_quantity || product.quantity,
                    saleStep(product)
                );
            },

            openWishlistBridge(id, qty) {
                if (typeof window.openWishlistModal !== 'function') {
                    console.error("La función global openWishlistModal no existe");
                    return;
                }

                // Ficha virtual Fegime: sin id hasta materializar el producto.
                if (!id && this.selectedProduct.is_fegime) {
                    materializeFegimeProduct(this.selectedProduct).then(realId => {
                        if (realId) window.openWishlistModal(realId, qty);
                    });
                    return;
                }

                window.openWishlistModal(id, qty);
            },

            scrollSlider(direction, refName = 'sliderEquivalentProducts') {
                const container = this.$refs[refName];
                if (!container) return;
                const scrollAmount = 300;
                container.scrollLeft += direction === 'left' ? -scrollAmount : scrollAmount;
            },

            generateTableData() {
                const grouped = {};

                // El espejo en PHP de formatEsNumber es $formatNumber
                // (ProductDetailsController.php:360-379), que arma la replica
                // SSR de esta misma tabla.
                this.localVariations.forEach(attr => {
                    attr.available_values.forEach(v => {
                        if (v.product_codes && v.product_codes.length > 0) {
                            v.product_codes.forEach(code => {
                                if (!grouped[code]) {
                                    const product = this.allProducts.find(p => p.erp_reference === code);
                                    grouped[code] = {
                                        product_code:           product?.alias_code ?? code,
                                        minimum:                Number(product ? product.minimum : 1),
                                        quantity:               Number(product?.minimum > 0 ? product.minimum : 1),
                                        id:                     product?.id ?? '',
                                        name:                   product?.name ?? '',
                                        price_without_discount: product ? formatEsNumber(product.price_without_discount_) : '',
                                        discount:               product ? formatEsNumber(product.discount_) : 0,
                                        netprice:               product ? formatEsNumber(product.price_) : '',
                                        images: (product?.images?.length > 0)
                                            ? product.images
                                            : [{ image: '/front/img/product_images/no-image.jpg' }],
                                        variations: {},
                                    };
                                }
                                grouped[code].variations[attr.name] = v.value;
                            });
                        }
                    });
                });

                this.tableData = Object.values(grouped);
            },

            selectVariantFromTable(item) {
                const product = this.allProducts.find(
                    p => p.erp_reference === item.product_code || p.alias_code === item.product_code
                );

                if (product) {
                    this.selectedProduct = product;
                    this.hydrateVariantDetails(product);

                    this.localVariations.forEach((variation) => {
                        const found = variation.available_values.find(value =>
                            value.product_codes.includes(product.erp_reference) ||
                            value.product_codes.includes(product.alias_code)
                        );
                        if (found) variation.selected_variation = found;
                    });

                    this.loadSelledProducts();

                    if (!this.selectedProduct.quantity || this.selectedProduct.quantity === 0) {
                        this.selectedProduct.quantity = this.selectedProduct.minimum > 0 ? this.selectedProduct.minimum : 1;
                    } else if (
                        this.selectedProduct.minimum > 0 &&
                        this.selectedProduct.quantity < this.selectedProduct.minimum
                    ) {
                        this.selectedProduct.quantity = this.selectedProduct.minimum;
                    }

                    window.scrollTo({ top: 0, behavior: 'smooth' });

                    this.isExpandedCharacteristics = false;
                    this.isExpandedDescription     = false;

                    if (product.slug) {
                        const currentUrl  = new URL(window.location);
                        let path          = currentUrl.pathname;
                        if (path.endsWith('/')) path = path.slice(0, -1);
                        const lastSlash   = path.lastIndexOf('/');
                        currentUrl.pathname = path.substring(0, lastSlash + 1) + product.slug;
                        window.history.pushState({}, '', currentUrl);
                    }
                }
            },

            isSelectedVariant(item) {
                return (
                    this.selectedProduct.erp_reference === item.product_code ||
                    this.selectedProduct.alias_code    === item.product_code
                );
            },

            increaseQuantity(item) {
                item.quantity = nextQuantity(item.quantity, saleStep(item));
            },

            decreaseQuantity(item) {
                item.quantity = previousQuantity(item.quantity, saleStep(item));
            },

            // Input de unidades de una fila de la tabla de variantes (@change).
            normalizeQuantityOf(item) {
                item.quantity = normalizeQuantity(item.quantity, saleStep(item));
            },

            applyVariantDetails(product, details) {
                product.description     = details.description;
                product.characteristics = details.characteristics;
                product.pdfs            = details.pdfs;
                // Si la variante seleccionada no tiene documentos, la pestana
                // "Documentos" desaparece: no puede quedarse activa.
                if (this.activeTab === 'docs'
                    && product === this.selectedProduct
                    && (details.pdfs || []).length === 0) {
                    this.activeTab = window.innerWidth >= 768 ? 'specs' : null;
                }
                if (details.images && details.images.length > 0) {
                    product.images = details.images;
                }
                product.details_loaded = true;
            },

            // allProducts viaja sin los campos pesados: al seleccionar una
            // variante se piden por fetch (mismo patrón que loadSelledProducts)
            // y se fusionan sobre el objeto de allProducts, que es el que está
            // como selectedProduct.
            async hydrateVariantDetails(product) {
                if (!product || product.details_loaded) return;

                const cached = this.variantDetailsCache[product.id];
                if (cached) {
                    this.applyVariantDetails(product, cached);
                    return;
                }

                const productId = product.id;

                try {
                    const locale = document.documentElement.lang || '';
                    const response = await fetch(`/get-variant-details/${productId}?locale=${locale}`);
                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                    const data = await response.json();
                    if (!data.details) return;

                    this.variantDetailsCache[productId] = data.details;
                    this.applyVariantDetails(product, data.details);
                } catch (error) {
                    console.error("Error al cargar los detalles de la variante:", error);
                }
            },

            async loadSelledProducts() {
                const productId = this.selectedProduct.id;

                // Ficha virtual Fegime: sin id de producto no hay "vendidos juntos".
                if (!productId) {
                    this.selled_products = [];
                    return;
                }

                if (this.currentSelledProductId === productId && this.loadingSelled) return;

                this.selled_products = [];

                try {
                    const response = await fetch(`/get-selled-together/${productId}`);
                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                    const data = await response.json();

                    if (this.selectedProduct.id !== productId) return; // race condition guard

                    this.selled_products = data.selledProducts || [];
                    this.selled_products.forEach(product => {
                        if (!product.custom_quantity) {
                            product.custom_quantity = this.getMinQty(product);
                        }
                    });
                } catch (error) {
                    console.error("Error al cargar los productos vendidos juntos:", error);
                    this.selled_products = [];
                }
            },

            async loadEquivalentProducts() {
                const token = document.head.querySelector('meta[name="csrf-token"]')?.content;
                try {
                    const response = await fetch(`/get-equivalent-products/${this.product.id}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type':     'application/json',
                            'Accept':           'application/json',
                            'X-Requested-With': 'XMLHttpRequest',
                            'X-CSRF-TOKEN':     token,
                        },
                    });

                    if (!response.ok) throw new Error(`Error en la petición: ${response.statusText}`);

                    const data = await response.json();
                    this.equivalent_products = data.equivalentProducts;
                    this.equivalent_products.forEach(product => {
                        if (!product.custom_quantity) {
                            product.custom_quantity = this.getMinQty(product);
                        }
                    });
                } catch (error) {
                    console.error("Error al cargar los productos equivalentes:", error);
                    this.loading = false;
                }
            },

            checkAvailability(currentVariation, candidateValue) {
                const candidateCodes = candidateValue.product_codes || [];

                const otherVariationsCodes = this.localVariations
                    .filter(v => v.id !== currentVariation.id && v.selected_variation)
                    .map(v => v.selected_variation.product_codes || []);

                if (otherVariationsCodes.length === 0) return true;

                const currentContextCodes = otherVariationsCodes.reduce((a, b) =>
                    a.filter(c => b.includes(c))
                );

                return candidateCodes.some(code => currentContextCodes.includes(code));
            },

            onChange(variation, value = null) {
                if (value) variation.selected_variation = value;

                const arraysOfCodes = this.localVariations.map(v =>
                    v.selected_variation?.product_codes ?? []
                );

                let possibleProducts = arraysOfCodes.reduce((a, b) => a.filter(c => b.includes(c)));
                let targetProductCode = null;

                if (possibleProducts.length > 0) {
                    targetProductCode = possibleProducts[0];
                } else {
                    const priorityVariationValue = value || variation.selected_variation;
                    if (priorityVariationValue?.product_codes?.length > 0) {
                        targetProductCode = priorityVariationValue.product_codes[0];
                        this.updateOtherVariationsToMatchProduct(targetProductCode, variation.id);
                    }
                }

                if (targetProductCode) this.findAndSetProductByCode(targetProductCode);
            },

            updateOtherVariationsToMatchProduct(productCode, changedVariationId) {
                const product = this.allProducts.find(p =>
                    p.erp_reference === productCode ||
                    p.product_code  === productCode ||
                    p.alias_code    === productCode
                );
                if (!product) return;

                this.localVariations.forEach(v => {
                    if (v.id === changedVariationId) return;
                    const matchingValue = v.available_values.find(val =>
                        val.product_codes.includes(productCode)
                    );
                    if (matchingValue) v.selected_variation = matchingValue;
                });
            },

            findAndSetProductByCode(code) {
                const product = this.allProducts.find(p =>
                    p.erp_reference === code ||
                    p.product_code  === code ||
                    p.alias_code    === code
                );
                if (product) {
                    this.selectedProduct = product;
                    this.hydrateVariantDetails(product);
                    if (!this.selectedProduct.quantity || this.selectedProduct.quantity === 0) {
                        this.selectedProduct.quantity = this.selectedProduct.minimum > 0 ? this.selectedProduct.minimum : 1;
                    }
                    if (product.slug) {
                        window.history.replaceState({}, '', product.slug);
                    }
                }
            },

            fmod(x, y) {
                const i      = parseInt(x * 100);
                const mod    = parseInt(y * 100);
                const parsed = (i % mod) / 100;
                return parsed;
            },

            openAddedProductModal() {
                const modal    = this.$refs.modalContainer;
                const backdrop = this.$refs.backdrop;
                const panel    = this.$refs.panel;
                if (!modal || !backdrop || !panel) return;
                modal.classList.remove('hidden');
                document.body.style.overflow = 'hidden';
                setTimeout(() => {
                    backdrop.classList.remove('opacity-0');
                    panel.classList.remove('translate-x-full');
                }, 10);
            },

            closeAddedProductModal() {
                const modal    = this.$refs.modalContainer;
                const backdrop = this.$refs.backdrop;
                const panel    = this.$refs.panel;
                if (!modal || !backdrop || !panel) return;
                backdrop.classList.add('opacity-0');
                panel.classList.add('translate-x-full');
                document.body.style.overflow = '';
                setTimeout(() => modal.classList.add('hidden'), 300);
            },

            addToShoppingCartFromTable(event, item) {
                event.preventDefault();
                event.stopPropagation();
            },

            handleQuantityChange(e) {
                const btn     = e.target.closest('button');
                const wrapper = btn.closest('.input-quantity-div');
                const input   = wrapper.querySelector('.input-quantity');
                const min     = parseInt(input.min) || 1;
                const step    = parseInt(input.step) || 1;
                let currentVal = parseInt(input.value) || min;

                if (btn.dataset.action === 'increase') {
                    input.value = currentVal + (step > 0 ? step : 1);
                } else {
                    input.value = currentVal - step >= min ? currentVal - step : min;
                }
            },

            addToCart(product) {
                // "PVP a consultar": articulo Fegime sin precio publicado. No
                // puede pedirse por la web (la linea iria a 0 €); el boton de
                // la ficha ya sale deshabilitado y el back lo para igualmente.
                if (hasNoSalePrice(product, product.price_ ?? product.price)) {
                    return;
                }

                // Cierre por delante del multiplo de venta: la cantidad que
                // viaja al carrito es siempre un multiplo, venga del input de
                // unidades, de la tabla de variantes o de la tecla Enter.
                const qty        = normalizeQuantity(
                    product.custom_quantity || product.quantity,
                    saleStep(product)
                );

                // Articulo Fegime (ficha virtual): se materializa como producto
                // web y sigue el flujo estandar del modal con el id real.
                if (product.is_fegime) {
                    materializeFegimeProduct(product).then(productId => {
                        if (!productId) return;

                        window.openAddCartModal(productId, qty, {
                            image: product.images?.[0]?.image,
                            name:  product.name,
                            ref:   product.alias_code ?? product.erp_reference,
                            ...(!product.no_price && {pvp: Number(product.price)}),
                        });
                    });
                    return;
                }

                const hasDiscount = parseFloat(product.discount_ ?? product.discount) > 0;

                const productInfo = {
                    image: product.images?.[0]?.image,
                    name:  product.name ?? product.translation.name,
                    ref:   product.alias_code ?? product.erp_reference ?? product.product_code,
                    ...(hasDiscount && {
                        desc:        Number((product.discount_ ?? product.discount).toString().replace(',', '.')),
                        net:         Number((product.price_   ?? product.netprice).toString().replace(',', '.')),
                        withoutdesc: Number((product.price_without_discount_ ?? product.price_without_discount).toString().replace(',', '.')),
                    }),
                    ...(!hasDiscount && {
                        pvp: Number((product.price ?? product.netprice).toString().replace(',', '.')),
                    }),
                };

                window.openAddCartModal(product.id, qty, productInfo);
            },

            addRelatedToCart(product) {
                const productToAdd = Object.assign({}, product);
                productToAdd.quantity = (parseFloat(product.minimum) > 0) ? parseFloat(product.minimum) : 1;
                this.addToCart(productToAdd);
            },

            incUnits() {
                this.selectedProduct.quantity = nextQuantity(
                    this.selectedProduct.quantity,
                    saleStep(this.selectedProduct)
                );
                this.$forceUpdate();
            },

            decUnits() {
                this.selectedProduct.quantity = previousQuantity(
                    this.selectedProduct.quantity,
                    saleStep(this.selectedProduct)
                );
                this.$forceUpdate();
            },

            // Cantidad escrita a mano en el input de unidades (@change): sube al
            // multiplo de venta mas cercano (multiplo 4, escribe 10 ⇒ 12). Sin
            // esto el input aceptaba cualquier numero y el carrito recibia una
            // cantidad que el ERP no puede servir.
            // Ver resources/js/utils/quantity.js.
            normalizeUnits() {
                this.selectedProduct.quantity = normalizeQuantity(
                    this.selectedProduct.quantity,
                    saleStep(this.selectedProduct)
                );
                this.$forceUpdate();
            },
        },
    };
</script>